import type { Navigator, Screen } from "../app/screen";
import { Api } from "../net/api";
import { pageShell, note } from "../ui/page";
import { setChoice } from "../app/session";
import { loadCardNames, cardName } from "../match/cards";
import { audio } from "../fx/audio";

/**
 * PLAY - choose where you climb, choose what you bring, then play.
 *
 * 🚨 **THIS SCREEN REPLACES A STUB THAT THE MENU'S PRIMARY BUTTON POINTED AT.** PLAY led to "not
 * built yet", which meant the main route into the game was a dead end while a match was reachable
 * only through CAMPAIGN.
 *
 * 🚨 **AND IT FIXES THE THING THAT "FELT WRONG".** Alex, 2026-08-14: *"did you import our 4 decks of
 * cards? are you sure the game rules, and format, and flow are correct? It still feels wrong."* The
 * decks were imported and correct - four of them, thirty cards each, one per realm hero. The client
 * never sent a deck id, so `MatchRegistry` fell back to `Decks[0]` and **every match used the same
 * deck**. See `app/session.ts` for the full trace.
 *
 * ⭐ **BOTH LISTS COME FROM THE SERVER, NOTHING IS ENUMERATED HERE.** Leagues from
 * `Lobby/AvailableLeagues`, decks from `Deck`. Alex is expanding the realms and minting the cards,
 * so a screen that hard-coded either would be wrong on the day that happens.
 *
 * ⚠️ **`leagueDescription`'s SECOND LINE IS AN ART KEY, NOT PROSE.** The server's own comment on the
 * route is emphatic: `LeaguesExtensions.GetArtURL` splits on the newline and takes line 2 as an
 * addressable key. Rendering the whole string would print `Flag_Starter_League` at the player.
 */
export const PlayScreen: Screen = {
  id: "leagues",

  // ⛔ The affordance contract. This screen exists because its predecessor shipped with neither.
  mustOffer: ["PLAY", "BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const shell = pageShell(nav, "PLAY", "choose where you climb");
    host.append(shell.el);

    const grid = document.createElement("div");
    grid.className = "play__grid";

    const leaguesCol = column("THE LEAGUES");
    const decksCol = column("YOUR DECKS");
    // ⭐ **A DOOR TO THE BUILDER, WHERE THE DECKS ARE.** Alex, 2026-08-15: *"on the play page where
    // it shows your decks there should be a link to the build deck page, in case a player decides to
    // build a new deck first and then does not need to go back to the main menu page."*
    //
    // ⚠️ It belongs in the column HEADER, beside the thing it acts on - a player who has just looked
    // at their four decks and wants a fifth is looking HERE, not at the bottom of the page.
    const buildLink = document.createElement("button");
    buildLink.type = "button";
    buildLink.className = "play__build";
    buildLink.dataset["label"] = "BUILD A DECK";
    buildLink.textContent = "BUILD A DECK";
    buildLink.title = "Open the deck builder. Your league choice is kept.";
    buildLink.addEventListener("click", () => { audio.play("click", 0.6); nav.go("decks"); });
    decksCol.head.append(buildLink);
    grid.append(leaguesCol.el, decksCol.el);

    const foot = document.createElement("div");
    foot.className = "play__foot";

    const playBtn = document.createElement("button");
    playBtn.type = "button";
    playBtn.className = "plate play__go";
    playBtn.dataset["label"] = "PLAY";
    const face = document.createElement("span");
    face.className = "plate__face";
    face.setAttribute("aria-hidden", "true");
    const label = document.createElement("span");
    label.className = "plate__label";
    label.textContent = "PLAY";
    playBtn.append(face, label);

    // ⚠️ **NO OPPONENT-SKILL CHOICE HERE ANY MORE.** It lived here briefly and moved to
    // `screens/practice.ts`: PLAY is the LADDER (a league, and whatever it gives you) and practice
    // is the sandbox (your deck, your choice of opposition, nothing recorded). One screen quietly
    // being both is what made practice invisible to a new player in the first place.
    // ⚠️ **DISABLED UNTIL BOTH ARE CHOSEN, AND IT SAYS WHY.** A control that is merely inert teaches
    // the player nothing; the hint under it names the missing half.
    const hint = note("Choose a league and a deck.");
    foot.append(hint, playBtn);

    shell.body.append(grid, foot);

    let leagueId: string | undefined;
    let deckId: string | undefined;
    let deckName: string | undefined;
    let leagueName: string | undefined;

    function refresh(): void {
      const ready = !!leagueId && !!deckId;
      playBtn.disabled = !ready;
      playBtn.classList.toggle("plate--disabled", !ready);
      hint.textContent = ready
        ? `${leagueName} with ${deckName}.`
        : !leagueId && !deckId ? "Choose a league and a deck."
        : !leagueId ? "Choose a league."
        : "Choose a deck.";
    }
    refresh();

    /*
      ⭐⭐ **QUICK MATCH LOOKS FOR A PERSON FIRST.**

      Alex, 2026-08-17: *"the 'Play QUICK MATCH' should match up with another player, and after a short
      time if it doesn't find a match it matches with a bot."*

      ⛔ **BEFORE THIS, PRESSING PLAY WENT STRAIGHT TO A BOT.** There was no queue and no attempt to
      find anybody: the AI was not a fallback, it was the entire feature, and the button said
      "quick match".

      ⭐ **THE BOT IS ANNOUNCED, ALWAYS.** `status: "bot"` is the server reporting that the search
      failed, and this screen says so before it starts one. An unannounced AI is precisely the defect
      that cost Alex a duel against a friend on this same day - the room set up correctly, both players
      pressed start, and both silently played a machine.

      ⚠️ **AND THE QUEUE SLOT IS RELEASED ON EVERY EXIT PATH** - navigating to the board, cancelling,
      or leaving the screen. A slot nobody is behind pairs the next real player against a closed tab.
    */
    let searching = false;
    let pollTimer: number | null = null;

    function stopSearch(): void {
      if (pollTimer !== null) { window.clearInterval(pollTimer); pollTimer = null; }
      searching = false;
    }

    // Exposed on the screen object below so `unmount` can release the slot too.
    cancelQueue = () => {
      stopSearch();
      void Api.quickMatchCancel().catch(() => { /* a slot we cannot release is reaped server-side */ });
    };

    function toBoard(): void {
      stopSearch();
      setChoice({ leagueId, leagueName, deckId, deckName });
      // The match screen asks `activeSession()` and JOINS when one exists, or starts a solo match when
      // it does not. Both outcomes land here and neither needs a second code path.
      nav.go("match");
    }

    async function tick(): Promise<void> {
      let r;
      try {
        r = await Api.quickMatch(deckId, leagueId);
      } catch (e) {
        stopSearch();
        playBtn.disabled = false;
        playBtn.classList.remove("plate--disabled");
        hint.textContent = `Could not reach matchmaking: ${describe(e)}`;
        return;
      }

      if (r.status === "matched") {
        audio.play("opponentFound");
        hint.textContent = r.opponent ? `Matched against ${r.opponent}.` : "Opponent found.";
        toBoard();
        return;
      }

      if (r.status === "bot") {
        // Say it plainly, and give it a beat so the words are actually read rather than flashed.
        hint.textContent = "No one else is searching right now. Starting a practice match.";
        stopSearch();
        window.setTimeout(toBoard, 1100);
        return;
      }

      const secs = Math.max(0, Math.ceil((r.fallbackInMs ?? 0) / 1000));
      // A countdown, not an unbounded spinner. A wait with no visible end is what makes a queue feel
      // broken even when it is working perfectly.
      hint.textContent = secs > 0
        ? `Looking for an opponent... ${secs}s`
        : "Looking for an opponent...";
    }

    playBtn.addEventListener("click", () => {
      if (!leagueId || !deckId || searching) return;
      audio.play("queue");
      searching = true;
      playBtn.disabled = true;
      playBtn.classList.add("plate--disabled");
      hint.textContent = "Looking for an opponent...";
      void tick();
      // 2s: fast enough that a pairing feels immediate, slow enough that a full lobby is not a
      // thundering herd. The server's reap grace is 30s, well clear of a missed beat or two.
      pollTimer = window.setInterval(() => void tick(), 2000);
    });

    // Card titles, so a deck can name its hero rather than printing `hero_arcadia_asclepius`.
    void loadCardNames();

    // ── the data ────────────────────────────────────────────────────────────────────────────────
    try {
      const leagues = await Api.leagues() as Array<{
        id?: string; leagueName?: string; leagueDescription?: string; isAllowed?: boolean;
      }>;
      leaguesCol.body.replaceChildren();
      if (!leagues.length) {
        leaguesCol.body.append(note("No leagues are open right now.", true));
      }
      for (const L of leagues) {
        if (!L?.id) continue;
        // Line 1 only - line 2 is the art key. See the class note.
        const desc = String(L.leagueDescription ?? "").split("\n")[0]?.trim() ?? "";
        const row = choiceRow(String(L.leagueName ?? "League"), desc, L.isAllowed !== false);
        row.addEventListener("click", () => {
          leagueId = L.id;
          leagueName = String(L.leagueName ?? "League");
          select(leaguesCol.body, row);
          audio.play("click", 0.6);
          refresh();
        });
        leaguesCol.body.append(row);
      }
    } catch (e) {
      leaguesCol.body.replaceChildren(note(`Could not load the leagues: ${describe(e)}`, true));
    }

    try {
      const decks = await Api.decks() as Array<{
        id?: string; name?: string; heroId?: string; deckValue?: number;
        ownedCardIds?: string[]; isPlayable?: boolean; invalidReason?: string | null;
      }>;
      decksCol.body.replaceChildren();
      if (!decks.length) {
        decksCol.body.append(note("You have no decks yet.", true));
      }
      for (const d of decks) {
        if (!d?.id) continue;
        const hero = cardName(d.heroId) || "";
        const count = Array.isArray(d.ownedCardIds) ? d.ownedCardIds.length : 0;
        // ⚠️ The card COUNT is what a player checks before committing, so it is on the row - not
        // buried in a builder they have to open.
        const sub = [hero, count ? `${count} cards` : "", d.invalidReason ?? ""]
          .filter(Boolean).join(" · ");
        const row = choiceRow(String(d.name ?? "Deck"), sub, d.isPlayable !== false);
        row.addEventListener("click", () => {
          if (d.isPlayable === false) return;
          deckId = d.id;
          deckName = String(d.name ?? "Deck");
          select(decksCol.body, row);
          audio.play("click", 0.6);
          refresh();
        });
        decksCol.body.append(row);
      }
    } catch (e) {
      decksCol.body.replaceChildren(note(`Could not load your decks: ${describe(e)}`, true));
    }
  },

  /**
   * ⚠️ **LEAVING THE SCREEN MUST RELEASE THE QUEUE SLOT.** Otherwise backing out of PLAY leaves you
   * queued: the next real player is paired against somebody who is not there, and their match then
   * waits for a client that will never connect. The server reaps stale entries as a backstop, but a
   * backstop is not an exit path (`engineering-invariants` §3b smell 10).
   */
  unmount(): void {
    cancelQueue?.();
    cancelQueue = null;
  },
};

/**
 * Set by `mount`, called by `unmount`.
 *
 * ⚠️ Module-scoped because `unmount` is a sibling of `mount` on the screen object and cannot see its
 * locals. Nulled on the way out so a second unmount cannot call into a dead closure.
 */
let cancelQueue: (() => void) | null = null;

function column(title: string): { el: HTMLElement; body: HTMLElement; head: HTMLElement } {
  const el = document.createElement("section");
  el.className = "play__col";
  const h = document.createElement("h2");
  h.className = "play__colhead";
  // ⚠️ A SPAN, not the heading's own text: the header now also carries an action (BUILD A DECK), and
  // setting `textContent` on the heading would wipe whatever a caller appended to it.
  const t = document.createElement("span");
  t.textContent = title;
  h.append(t);
  const body = document.createElement("div");
  body.className = "play__list";
  body.append(note("Loading..."));
  el.append(h, body);
  return { el, body, head: h };
}

/**
 * One selectable row.
 *
 * ⚠️ A `<button>`, not a styled div: it has to be reachable by keyboard and announce itself, and
 * `data-label` is what the affordance check reads.
 */
function choiceRow(title: string, sub: string, enabled: boolean): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "play__row";
  b.dataset["label"] = title;
  b.disabled = !enabled;
  if (!enabled) b.classList.add("play__row--locked");

  const t = document.createElement("span");
  t.className = "play__rowtitle";
  // ⚠️ textContent: a deck name is player-supplied text.
  t.textContent = title;
  b.append(t);

  if (sub) {
    const s = document.createElement("span");
    s.className = "play__rowsub";
    s.textContent = sub;
    b.append(s);
  }
  return b;
}

function select(list: HTMLElement, row: HTMLElement): void {
  for (const c of Array.from(list.children)) c.classList.remove("play__row--on");
  row.classList.add("play__row--on");
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
